import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → Staff Changes / Updates → New Advisor / CSA / Staff and
 * Terminated Advisor / CSA / Staff.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "New Staff Changes / Updates Screen"
 * (regions "New Staff Information" and "New Staff Member Information") and
 * "Terminated Stafff Screen" (regions "Terminated Staff Details" and
 * "Terminated Staff Member Information"). Each sub-category is a single
 * screen ending in the notice and Acknowledgement. The case header shows
 * Office, not Household. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Staff Changes / Updates',
    subCategory: 'New Advisor / CSA / Staff or Terminated Advisor / CSA / Staff',
    subject: subjectRow('New staff member - CSA', { noAccount: true }),
    lookups: [['Office', <>Your own office or team - there is no client on a staff request. The screen reads: <em>Please confirm the Office/Team you are with. If you do not see your Office/Team, please reach out to the Operations Team.</em></>]],
    note: (
      <>
        <strong>New and Terminated are different questions</strong> - both are listed below, under their own
        headings.
      </>
    ),
  }),
  {
    title: 'Answer the staff questions',
    body: (
      <p style={{ margin: 0 }}>
        Questions reveal themselves one at a time based on your answers. For a new hire you are asked whether
        the person is registered, their licences and certifications, and which systems they need. For a
        departure you are asked for the termination date, whether the person was registered, and their role.
        Every question in the reference below is required once it appears.
      </p>
    ),
  },
  {
    title: "Enter the person's details",
    body: (
      <p style={{ margin: 0 }}>
        Once the last question is answered, a second section opens on the same screen for the staff
        member&rsquo;s own details - name, phone, email and, for a new hire, SSN, date of birth, role, start date
        and home address. It ends with a details box that becomes the case description Operations reads first.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the notice under the details box, select <em>I have read, understand, and agree to the above
        information</em> under Acknowledgement and click Next. FSC creates the case and opens it. Note the case
        number for follow-up.
      </p>
    ),
  },
];

// Every question on the two Staff Changes / Updates screens, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'New Advisor / CSA / Staff - New Staff Information',
    q: 'Is this a Registered/Access Person? (Advisor or CSA, for example)',
    when: 'Always - this is the first question',
    options: ['Yes (For Advisor)', 'Yes (For CSA / Paraplanner)', 'No (Not Registered/Access Person)'],
  },
  {
    q: 'Please list all Masters / G-Numbers',
    when: 'Only if you chose Yes (For Advisor)',
  },
  {
    q: 'Does the new staff member have any of the following? (Check all that apply)',
    when: 'Once the registered question is answered',
    options: [
      'SIE', 'Series 6', 'Series 7', 'Series 63', 'Series 65', 'Series 66', 'Life Insurance',
      'Accident & Health Insurance', 'Long Term Care Certification', 'Annuity Suitability', 'CFP', 'ChFC', 'Other',
    ],
    note: 'Tick every one that applies.',
  },
  {
    q: 'Other Details',
    when: 'Only if you ticked Other',
  },
  {
    q: 'Which Systems will they need access to?',
    when: 'Once at least one licence or certification is ticked',
    options: ['Schwab', 'Fidelity', 'Orion', 'Zocks', 'eMoney', 'Salesforce', 'Other'],
    note: 'Pick one here, then list every other system they need in the next question.',
  },
  {
    q: 'Please list any other relevant information or systems they may need access to here...',
    when: 'Once a system is chosen',
    note: 'Name every system beyond the one picked above.',
  },
  {
    screen: 'New Advisor / CSA / Staff - New Staff Member Information',
    q: 'First Name · Last Name',
    when: 'Once the systems box above has text',
  },
  {
    q: 'SSN / Tax ID',
    when: 'In the New Staff Member Information section',
    note: <>Numbers only, no dashes. The error message reads <em>Must be 9 numbers entered, no text</em>.</>,
  },
  {
    q: 'Date of Birth',
    when: 'In the New Staff Member Information section',
  },
  {
    q: 'Phone · Email',
    when: 'In the New Staff Member Information section',
    note: 'The new staff member’s own contact details. Both required.',
  },
  {
    q: 'Role / Title',
    when: 'In the New Staff Member Information section',
  },
  {
    q: 'Start Date',
    when: 'In the New Staff Member Information section',
    note: 'Defaults to today - change it to the actual start date.',
  },
  {
    q: 'Address',
    when: 'In the New Staff Member Information section',
    note: 'A standard address block. Required.',
  },
  {
    q: 'Additional Instructions',
    when: 'In the New Staff Member Information section',
    note: 'Required. Becomes the case description.',
  },
  {
    screen: 'Terminated Advisor / CSA / Staff - Terminated Staff Details',
    q: 'Termination Date',
    when: 'Always - this is the first question',
  },
  {
    q: 'Was this a Registered/Access Person? (Advisor, CSA, or Paraplanner, for example)',
    when: 'Once a termination date is entered',
    options: ['Yes (This was an Advisor)', 'Yes (This was a CSA/Paraplanner/Other)', 'No (This was not a Registered/Access Person)'],
  },
  {
    q: 'Please list all Masters / G-Numbers',
    when: 'Only if you chose Yes (This was an Advisor)',
  },
  {
    q: 'What was their Role? (Lead Advisor, Operations Manager, Marketing Assistant? Etc. Etc..)',
    when: 'Once the registered question is answered - for an advisor, once the Masters / G-Numbers are listed',
  },
  {
    screen: 'Terminated Advisor / CSA / Staff - Terminated Staff Member Information',
    q: 'First Name · Last Name',
    when: 'Once the role is entered',
  },
  {
    q: 'Phone · Email',
    when: 'In the Terminated Staff Member Information section',
    note: 'Both required.',
  },
  {
    q: 'Please provide as much information for the Operations Team as possible...',
    when: 'In the Terminated Staff Member Information section',
    note: 'Required. Becomes the case description.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Staff Changes / Updates'),
  {
    h: "I don't see my office in the Office picker",
    p: ['The screen asks you to reach out to the Operations Team if your Office/Team is not listed.'],
  },
  {
    h: "The details section and Acknowledgement haven't appeared",
    p: [
      'For a new hire they stay hidden until Please list any other relevant information or systems they may need access to here... has something in it. For a departure they wait for What was their Role?',
    ],
  },
  {
    h: 'There is no Household picker',
    p: ['That is expected. Staff requests are about your office, not a client, so the header asks for Office instead.'],
  },
];

const ROWS = [
  ['Incomplete or unclear instructions',
   'Prosperity may request additional verification or documentation, or decline instructions that are incomplete, unclear, or inconsistent with firm policy.'],
];

export default function StaffChanges() {
  return (
    <GuidePage
      title="Staff Changes and Updates"
      icon="people"
      path="Operations → Staff Changes / Updates → New or Terminated Advisor / CSA / Staff"
      minutes={4}
      intro="How to tell the Prosperity Operations team about a new hire or a departure in your office - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and every question is required once it appears.</Para>
        <Bullets items={[
          <><strong>For a new hire</strong>: full name, SSN or Tax ID, date of birth, phone, email, home address, role or title and start date; whether they are a Registered/Access Person; their licences and certifications; and every system they will need.</>,
          <><strong>For a departure</strong>: full name, phone, email, termination date and role, and whether they were a Registered/Access Person.</>,
          <><strong>Masters / G-Numbers</strong> for an advisor, new or departing.</>,
        ]} />
        <Callout>
          The case is about your office, not a client, so there is no account number. Keep the subject a short
          plain description, such as &ldquo;New staff member - CSA&rdquo; or &ldquo;Terminated staff member -
          Advisor&rdquo;.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you speak to the new hire, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        confirmations={false}
        before={(
          <Para>
            FSC creates the case, shares it with you and with your office, and opens it. The submit screen shows
            the same Operations and Trading notice as client requests; its parts on trading, SMAs and raising cash
            are written for client accounts.
          </Para>
        )}
        rows={ROWS}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe registration, licensing or system
        access steps that follow a hire or departure.
      </Footnote>
    </GuidePage>
  );
}
